// src/pages/DailyReportAccess.jsx
// ⚠️ TEMPORÁRIO — acesso separado com login/senha fixos, pra quem não tem
// conta no sistema. Remover esta rota (App.jsx) + este arquivo quando não
// precisar mais, junto com miniboss-backend/temp_daily_report_access.py.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogContent, IconButton, CircularProgress, TablePagination,
} from '@mui/material';
import { X, FileText, Lock } from 'lucide-react';
import axios from 'axios';
import DailyActivityReportView from '../components/DailyActivityReportView';

const API_URL = process.env.REACT_APP_API_URL;
const SESSION_KEY = 'dailyReportTempAuth';

// ── Register palette — a records-office ledger, not a dashboard. ──
const PAPER = '#F7F5F0';
const CARD = '#FFFFFF';
const INK = '#1A2233';
const INK_SOFT = '#5B6472';
const RULE = '#DEDACD';
const GREEN = '#276749';   // registrar's stamp ink — "signed"
const RED = '#A23B33';     // flag ink — "incident"
const MUTED = '#A6A196';   // "missing"

const FONT_DISPLAY = '"Source Serif 4", Georgia, serif';
const FONT_MONO = '"IBM Plex Mono", "Courier New", monospace';
const FONT_BODY = '"Inter", "Roboto", -apple-system, sans-serif';

const FONT_LINK_ID = 'daily-report-register-fonts';

function RegisterFonts() {
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,600;8..60,700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500;600&display=swap';
    document.head.appendChild(link);
  }, []);
  return null;
}

const todayISO = () => new Date().toISOString().slice(0, 10);
const weekAgoISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return d.toISOString().slice(0, 10);
};

// The one signature element: a rotated, double-ruled stamp, the way a
// registrar would mark a physical logbook. Reused for every signed state.
function StampMark({ label, tone = 'signed' }) {
  const color = { signed: GREEN, flagged: RED, missing: MUTED }[tone];
  const isMark = tone !== 'missing';
  return (
    <Box component="span" sx={{
      position: 'relative', display: 'inline-flex', alignItems: 'center',
      fontFamily: FONT_MONO, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.09em',
      textTransform: 'uppercase', color,
      border: `1.5px solid ${color}`, borderRadius: '2px',
      padding: '3px 9px', lineHeight: 1.4,
      transform: isMark ? 'rotate(-2deg)' : 'none',
      opacity: isMark ? 1 : 0.75,
      '&::after': isMark ? {
        content: '""', position: 'absolute', inset: '2px',
        border: `1px solid ${color}`, borderRadius: '1px', opacity: 0.55,
      } : undefined,
    }}>
      {label}
    </Box>
  );
}

function FieldFrame({ label, children }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{
        fontFamily: FONT_MONO, fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.12em',
        color: INK_SOFT, textTransform: 'uppercase',
      }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function LoginForm({ onSuccess }) {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/acesso/dailyreport-login`, { user, pass });
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ user, pass }));
      onSuccess({ user, pass });
    } catch {
      setError('Usuário ou senha inválidos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      bgcolor: PAPER, backgroundImage: `linear-gradient(180deg, ${PAPER} 0%, #F0ECE0 100%)`,
      fontFamily: FONT_BODY, px: 2,
    }}>
      <Box sx={{
        width: 380, bgcolor: CARD, border: `1px solid ${RULE}`, borderRadius: '3px',
        boxShadow: '0 1px 2px rgba(26,34,51,0.06), 0 16px 40px rgba(26,34,51,0.08)',
        p: 4.5,
      }}>
        <Typography sx={{
          fontFamily: FONT_MONO, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.16em',
          color: INK_SOFT, textTransform: 'uppercase', mb: 0.75,
        }}>
          MiniBoss Field Records
        </Typography>
        <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '1.6rem', color: INK, mb: 2 }}>
          Access Portal
        </Typography>
        <Box sx={{ borderTop: `3px double ${RULE}`, mb: 3 }} />

        <form onSubmit={submit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            <FieldFrame label="Access ID">
              <TextField
                variant="standard" value={user} onChange={e => setUser(e.target.value)} autoFocus fullWidth
                InputProps={{ sx: { fontFamily: FONT_MONO, fontSize: '0.95rem', color: INK } }}
                sx={{ '& .MuiInput-underline:before': { borderBottomColor: RULE }, '& .MuiInput-underline:hover:before': { borderBottomColor: INK_SOFT } }}
              />
            </FieldFrame>
            <FieldFrame label="Passphrase">
              <TextField
                variant="standard" type="password" value={pass} onChange={e => setPass(e.target.value)} fullWidth
                InputProps={{ sx: { fontFamily: FONT_MONO, fontSize: '0.95rem', color: INK } }}
                sx={{ '& .MuiInput-underline:before': { borderBottomColor: RULE }, '& .MuiInput-underline:hover:before': { borderBottomColor: INK_SOFT } }}
              />
            </FieldFrame>

            {error && (
              <Typography sx={{
                fontFamily: FONT_MONO, fontSize: '0.75rem', color: RED,
                borderLeft: `2px solid ${RED}`, pl: 1.25, py: 0.25,
              }}>
                {error}
              </Typography>
            )}

            <Button
              type="submit" disabled={loading} fullWidth disableElevation
              startIcon={!loading ? <Lock size={14} /> : null}
              sx={{
                mt: 1, bgcolor: INK, color: PAPER, borderRadius: '2px', py: 1.1,
                fontFamily: FONT_MONO, fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.12em',
                textTransform: 'uppercase', '&:hover': { bgcolor: '#0F1520' },
                '&.Mui-disabled': { bgcolor: '#8890A0', color: PAPER },
              }}
            >
              {loading ? <CircularProgress size={16} sx={{ color: PAPER }} /> : 'Enter Register'}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
}

function ReportsView({ auth }) {
  const [startDate, setStartDate] = useState(weekAgoISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [hotelId, setHotelId] = useState('');
  const [employeeId, setEmployeeId] = useState('');

  const [hotels, setHotels] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/acesso/dailyreport-filters`, { params: auth })
      .then(res => { setHotels(res.data.hotels); setEmployees(res.data.employees); })
      .catch(() => {});
  }, [auth]);

  const fetchRows = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);
    axios.get(`${API_URL}/acesso/dailyreport-data`, {
      params: { ...auth, start_date: startDate, end_date: endDate, hotel_id: hotelId || undefined, employee_id: employeeId || undefined },
    })
      .then(res => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [auth, startDate, endDate, hotelId, employeeId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setPage(0); }, [startDate, endDate, hotelId, employeeId]);

  const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openDetail = (id) => {
    setDetailLoading(true);
    axios.get(`${API_URL}/acesso/dailyreport-data/${id}`, { params: auth })
      .then(res => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  const selectSx = {
    fontFamily: FONT_MONO, fontSize: '0.85rem', color: INK,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: RULE },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: INK_SOFT },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN },
  };
  const labelSx = { fontFamily: FONT_MONO, fontSize: '0.75rem', color: INK_SOFT };

  return (
    <Box sx={{ height: '100vh', overflowY: 'auto', bgcolor: PAPER, fontFamily: FONT_BODY }}>
      <Box sx={{ maxWidth: 1080, mx: 'auto', p: { xs: 2.5, sm: 4 }, display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* ── Masthead ── */}
        <Box sx={{ borderBottom: `3px double ${RULE}`, pb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography sx={{
                fontFamily: FONT_MONO, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.16em',
                color: INK_SOFT, textTransform: 'uppercase', mb: 0.5,
                display: 'flex', alignItems: 'center', gap: 0.75,
              }}>
                <FileText size={13} /> MiniBoss Field Records · Access Portal
              </Typography>
              <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: { xs: '1.7rem', sm: '2.1rem' }, color: INK }}>
                Daily Operations Register
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.7rem', color: INK_SOFT }}>
              {rows.length} record{rows.length === 1 ? '' : 's'} on file
            </Typography>
          </Box>
        </Box>

        {/* ── Report period / metadata ── */}
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 3,
          p: 2.5, borderRadius: '3px', bgcolor: CARD, border: `1px solid ${RULE}`,
        }}>
          <FieldFrame label="Report Period — From">
            <input type="date" value={startDate} max={endDate} onChange={e => setStartDate(e.target.value)}
              style={{ background: 'transparent', border: 'none', borderBottom: `1.5px solid ${RULE}`, fontFamily: FONT_MONO, fontSize: '0.85rem', color: INK, outline: 'none', padding: '2px 0' }} />
          </FieldFrame>
          <FieldFrame label="To">
            <input type="date" value={endDate} min={startDate} onChange={e => setEndDate(e.target.value)}
              style={{ background: 'transparent', border: 'none', borderBottom: `1.5px solid ${RULE}`, fontFamily: FONT_MONO, fontSize: '0.85rem', color: INK, outline: 'none', padding: '2px 0' }} />
          </FieldFrame>

          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel sx={labelSx}>Location</InputLabel>
            <Select label="Location" value={hotelId} onChange={e => setHotelId(e.target.value)} sx={selectSx}>
              <MenuItem value="" sx={{ fontFamily: FONT_MONO, fontSize: '0.85rem' }}>All locations</MenuItem>
              {hotels.map(h => <MenuItem key={h.id} value={h.id} sx={{ fontFamily: FONT_MONO, fontSize: '0.85rem' }}>{h.name}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel sx={labelSx}>Personnel</InputLabel>
            <Select label="Personnel" value={employeeId} onChange={e => setEmployeeId(e.target.value)} sx={selectSx}>
              <MenuItem value="" sx={{ fontFamily: FONT_MONO, fontSize: '0.85rem' }}>All personnel</MenuItem>
              {employees.map(e => <MenuItem key={e.id} value={e.id} sx={{ fontFamily: FONT_MONO, fontSize: '0.85rem' }}>{e.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {/* ── Register table ── */}
        <TableContainer sx={{ bgcolor: CARD, border: `1px solid ${RULE}`, borderRadius: '3px' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Date', 'Location', 'Personnel', 'Status', 'Incidents', 'Filed At'].map(h => (
                  <TableCell key={h} sx={{
                    fontFamily: FONT_MONO, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.1em',
                    color: INK_SOFT, textTransform: 'uppercase', borderBottom: `1.5px solid ${RULE}`,
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, border: 0 }}><CircularProgress size={22} sx={{ color: INK_SOFT }} /></TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, border: 0, fontFamily: FONT_MONO, fontSize: '0.8rem', color: INK_SOFT }}>No records on file for this period.</TableCell></TableRow>
              )}
              {!loading && pagedRows.map(r => (
                <TableRow
                  key={r.id} hover onClick={() => openDetail(r.id)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: `${GREEN}0A` }, '& td': { borderColor: RULE, fontFamily: FONT_BODY, fontSize: '0.85rem', color: INK } }}
                >
                  <TableCell sx={{ fontFamily: FONT_MONO, fontSize: '0.82rem' }}>{r.entry_date}</TableCell>
                  <TableCell>{r.hotel_name}</TableCell>
                  <TableCell>{r.employee_name}</TableCell>
                  <TableCell>
                    <StampMark label={r.is_complete ? 'Signed' : 'Incomplete'} tone={r.is_complete ? 'signed' : 'missing'} />
                  </TableCell>
                  <TableCell>
                    {r.has_incidents ? <StampMark label="Flagged" tone="flagged" /> : <Box component="span" sx={{ color: MUTED, fontFamily: FONT_MONO }}>—</Box>}
                  </TableCell>
                  <TableCell sx={{ fontFamily: FONT_MONO, fontSize: '0.78rem', color: INK_SOFT }}>{r.completed_at || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{ fontFamily: FONT_MONO, borderTop: `1px solid ${RULE}`, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily: FONT_MONO, fontSize: '0.75rem' } }}
          />
        </TableContainer>

        <Dialog open={!!detail || detailLoading} onClose={() => setDetail(null)} maxWidth="md" fullWidth
          PaperProps={{ sx: { bgcolor: PAPER, borderRadius: '3px' } }}>
          <DialogContent sx={{ position: 'relative', p: detail ? 2.5 : 4, display: 'flex', justifyContent: 'center' }}>
            <IconButton onClick={() => setDetail(null)} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'white', boxShadow: 1 }} size="small">
              <X size={18} />
            </IconButton>
            {detailLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: INK_SOFT }} /></Box>}
            {!detailLoading && detail && <DailyActivityReportView reportData={detail} signatureSvg={detail.signature_svg} />}
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
}

export default function DailyReportAccess() {
  const [auth, setAuth] = useState(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored ? JSON.parse(stored) : null;
  });

  return (
    <>
      <RegisterFonts />
      {!auth ? <LoginForm onSuccess={setAuth} /> : <ReportsView auth={auth} />}
    </>
  );
}
